// src/pages/BlogPost/BlogPost.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogAPI } from '../../api/blog.js';
import { Breadcrumbs } from '../../components/common/Breadcrumbs/Breadcrumbs';
import { Loader } from '../../components/ui/Loader/Loader.jsx';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Input } from '../../components/ui/Input/Input.jsx';
import { formatDate } from '../../utils/formatters.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useToast } from '../../hooks/useToast.js';

export const BlogPost = () => {
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const { success, error } = useToast();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');

  useEffect(() => {
    loadPost();
  }, [id]);

  const loadPost = async () => {
    setIsLoading(true);
    try {
      const [postRes, commentsRes] = await Promise.all([
        blogAPI.getById(id),
        blogAPI.getComments(id),
      ]);
      setPost(postRes.data.data);
      setComments(commentsRes.data.data || []);
    } catch (error) {
      console.error('Error loading post:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) {
      error('Введите текст комментария');
      return;
    }

    try {
      await blogAPI.addComment(id, {
        content: commentText,
        ...(isAuthenticated ? {} : { guest_name: guestName, guest_email: guestEmail }),
      });
      success('Комментарий добавлен');
      setCommentText('');
      loadPost();
    } catch (err) {
      error(err.response?.data?.message || 'Не удалось добавить комментарий');
    }
  };

  if (isLoading) {
    return <Loader text="Загрузка статьи..." />;
  }

  if (!post) {
    return (
      <div className="container" style={{ paddingTop: '60px', textAlign: 'center' }}>
        <h2>Статья не найдена</h2>
        <Link to="/blog">
          <Button style={{ marginTop: '16px' }}>Вернуться в блог</Button>
        </Link>
      </div>
    );
  }

  const breadcrumbs = [
    { label: 'Блог', path: '/blog' },
    { label: post.title, path: `/blog/${post.id}` },
  ];

  return (
    <div style={{ backgroundColor: 'rgb(var(--c-bg))', minHeight: '100vh', paddingBottom: '40px' }}>
      <div className="container" style={{ paddingTop: '24px' }}>
        <Breadcrumbs items={breadcrumbs} />

        <div style={{
          marginTop: '32px',
          backgroundColor: 'rgb(var(--c-surface))',
          borderRadius: '20px',
          border: '1px solid rgb(var(--c-border))',
          overflow: 'hidden',
          maxWidth: '800px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          {/* Обложка */}
          {post.cover_image && (
            <div style={{
              backgroundColor: 'rgb(var(--c-surface-2))',
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgb(var(--c-text-secondary))',
              fontSize: '16px',
            }}>
              Изображение
            </div>
          )}

          {/* Контент */}
          <div style={{ padding: '40px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
              marginBottom: '16px',
            }}>
              <span style={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'rgb(var(--c-accent))',
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
              }}>
                {post.category}
              </span>
              <span style={{
                fontSize: '14px',
                color: 'rgb(var(--c-text-secondary))',
              }}>
                {formatDate(post.published_at || post.createdAt)} • 👁 {post.view_count || 0}
              </span>
            </div>

            <h1 style={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'rgb(var(--c-text))',
              marginBottom: '16px',
            }}>
              {post.title}
            </h1>

            <div style={{
              fontSize: '16px',
              fontWeight: 300,
              color: 'rgb(var(--c-text-secondary))',
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap',
            }}>
              {post.content}
            </div>

            {/* Теги */}
            {post.tags && post.tags.length > 0 && (
              <div style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                marginTop: '24px',
                paddingTop: '24px',
                borderTop: '1px solid rgb(var(--c-border))',
              }}>
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      padding: '4px 12px',
                      backgroundColor: 'rgb(var(--c-surface-2))',
                      borderRadius: '100px',
                      fontSize: '12px',
                      color: 'rgb(var(--c-text-secondary))',
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Комментарии */}
        <div style={{
          marginTop: '32px',
          maxWidth: '800px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 600,
            color: 'rgb(var(--c-text))',
            marginBottom: '24px',
          }}>
            Комментарии ({comments.length})
          </h2>

          {/* Форма комментария */}
          <form onSubmit={handleSubmitComment} style={{
            backgroundColor: 'rgb(var(--c-surface))',
            border: '1px solid rgb(var(--c-border))',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: 'rgb(var(--c-text))',
              marginBottom: '16px',
            }}>
              Оставить комментарий
            </h3>

            {!isAuthenticated && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Input
                  label="Имя"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  required
                />
                <Input
                  label="Email"
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  required
                />
              </div>
            )}

            <div style={{ marginTop: '12px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: 'rgb(var(--c-text))',
                marginBottom: '4px',
              }}>
                Комментарий *
              </label>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Ваш комментарий..."
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid rgb(var(--c-border))',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical',
                  minHeight: '80px',
                  fontFamily: 'inherit',
                  backgroundColor: 'rgb(var(--c-bg))',
                  color: 'rgb(var(--c-text))',
                }}
                required
              />
            </div>

            <Button type="submit" style={{ marginTop: '12px' }}>
              Отправить
            </Button>
          </form>

          {/* Список комментариев */}
          {comments.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 0',
              color: 'rgb(var(--c-text-secondary))',
              backgroundColor: 'rgb(var(--c-surface))',
              borderRadius: '16px',
              border: '1px solid rgb(var(--c-border))',
            }}>
              Пока нет комментариев. Будьте первым!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    backgroundColor: 'rgb(var(--c-surface))',
                    border: '1px solid rgb(var(--c-border))',
                    borderRadius: '16px',
                    padding: '20px',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: 'rgb(var(--c-text))',
                      }}>
                        {comment.User?.first_name || comment.guest_name || 'Аноним'}
                      </span>
                    </div>
                    <span style={{
                      fontSize: '12px',
                      color: 'rgb(var(--c-text-secondary))',
                    }}>
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>
                  <p style={{
                    fontSize: '15px',
                    fontWeight: 300,
                    color: 'rgb(var(--c-text-secondary))',
                    lineHeight: 1.6,
                  }}>
                    {comment.content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};